import type { OverlayDrawerKind } from '@/app/navigation/navigation-state';

/**
 * Permission contract for an overlay drawer kind.
 *
 * - `resource` is the resource string passed to `hasPermission(resource, action)`.
 * - `edit` / `delete` are the action strings (typically 'EDIT' / 'DELETE').
 *
 * Set the entry to `null` when the kind is rendered without app-level
 * permission gating — either the drawer itself enforces access internally
 * (e.g. AtividadeDrawer) or the kind is reserved for a phase that hasn't
 * shipped yet.
 */
export type DrawerPermissionEntry = {
  resource: string;
  edit: string;
  delete: string;
};

/**
 * Centralized kind → permission contract mapping.
 *
 * Adding a kind to `OverlayDrawerKind` without an entry here is a typecheck
 * error (Record key exhaustiveness). New phases populate their kinds when
 * their renderer ships; until then, set `null` and the host treats the
 * drawer as ungated.
 */
export const DRAWER_PERMISSIONS: Record<
  OverlayDrawerKind,
  DrawerPermissionEntry | null
> = {
  // Phase 1 — already rendered
  obra: { resource: 'OBRA', edit: 'EDIT', delete: 'DELETE' },
  jornada: { resource: 'OBRA_JORNADA', edit: 'EDIT', delete: 'DELETE' },
  obra_jornada: { resource: 'OBRA_JORNADA', edit: 'EDIT', delete: 'DELETE' },
  projeto: { resource: 'PROJETO', edit: 'EDIT', delete: 'DELETE' },
  atividade: null, // AtividadeDrawer enforces access internally
  oportunidade: null, // OportunidadeDrawer enforces access internally
  requisicao: { resource: 'REQUISICAO', edit: 'EDIT', delete: 'DELETE' },
  ordem_compra: { resource: 'ORDEM_COMPRA', edit: 'EDIT', delete: 'DELETE' },

  // Phase 3
  pessoa: { resource: 'PESSOA', edit: 'EDIT', delete: 'DELETE' },

  // Phase 4 — OS
  os: { resource: 'OS', edit: 'EDIT', delete: 'DELETE' },
  // Servico/Vistoria are subresources of OS — no dedicated permission tables;
  // the in-drawer code already calls `hasPermission('OS', 'EDIT')` directly.
  os_servico: { resource: 'OS', edit: 'EDIT', delete: 'DELETE' },
  os_vistoria: { resource: 'OS', edit: 'EDIT', delete: 'DELETE' },
  // Report review is a side panel without create/delete actions — drawer
  // gates its own writes internally; no app-level gating needed.
  os_report_review: null,
  // Requisicao opened from inside an OS uses the suprimentos REQUISICAO
  // resource, same as the standalone Requisicao kind (Phase 5).
  os_requisicao: { resource: 'REQUISICAO', edit: 'EDIT', delete: 'DELETE' },

  // Phase 5 — Suprimentos
  marca: { resource: 'MARCA', edit: 'EDIT', delete: 'DELETE' },
  produto_categoria: { resource: 'PRODUTO_CATEGORIA', edit: 'EDIT', delete: 'DELETE' },
  produto_subcategoria: { resource: 'PRODUTO_SUBCATEGORIA', edit: 'EDIT', delete: 'DELETE' },
  produto_tipo: { resource: 'PRODUTO_TIPO', edit: 'EDIT', delete: 'DELETE' },
  // Recurso real no DB é `PRODUTO_UNIDADE` (não PRODUTO_UNIDADE_MEDIDA);
  // matches `recurso="PRODUTO_UNIDADE"` no parent ProdutoUnidadeMedida.tsx.
  produto_unidade_medida: { resource: 'PRODUTO_UNIDADE', edit: 'EDIT', delete: 'DELETE' },
  // Recurso real no DB é `PRODUTO_MARGEM` (não PRODUTO_MARGEM_TIPO);
  // matches `recurso="PRODUTO_MARGEM"` no parent ProdutoMargemTipo.tsx.
  produto_margem_tipo: { resource: 'PRODUTO_MARGEM', edit: 'EDIT', delete: 'DELETE' },
  movimentacao_estoque: { resource: 'MOVIMENTACAO_ESTOQUE', edit: 'EDIT', delete: 'DELETE' },
  // Orcamento has no dedicated `recurso` row — it belongs to an OC and
  // historically inherits ORDEM_COMPRA permissions (matches the local
  // permissions={permissions} pass-through inside OrdemCompraDrawer).
  orcamento: { resource: 'ORDEM_COMPRA', edit: 'EDIT', delete: 'DELETE' },
  cat: { resource: 'CAT', edit: 'EDIT', delete: 'DELETE' },
  ordem_compra_status: { resource: 'ORDEM_COMPRA_STATUS', edit: 'EDIT', delete: 'DELETE' },

  // Phase 6 — Comercial
  vendas: { resource: 'VENDAS', edit: 'EDIT', delete: 'DELETE' },
  // View only. EDIT and DELETE are not granted, so the drawer stays read-only.
  proposta_resultado: { resource: 'PROPOSTA_RESULTADO', edit: 'EDIT', delete: 'DELETE' },
  empreendimento: { resource: 'EMPREENDIMENTO', edit: 'EDIT', delete: 'DELETE' },
  oportunidade_status: { resource: 'OPORTUNIDADE_STATUS', edit: 'EDIT', delete: 'DELETE' },
  // Comissao_detalhe não tem recurso dedicado no DB (`recurso` table); herda
  // COMISSAO. Drawer self-fetches por id e absorbe canApprove via useAuth +
  // ConfirmActionModal + RPC fn_fechar_comissao (Phase 10c re-architecture).
  comissao_detalhe: { resource: 'COMISSAO', edit: 'EDIT', delete: 'DELETE' },

  // Phase 7 — Atividade
  atividade_area: { resource: 'ATIVIDADE_AREA', edit: 'EDIT', delete: 'DELETE' },
  atividade_tipo: { resource: 'ATIVIDADE_TIPO', edit: 'EDIT', delete: 'DELETE' },

  // Phase 8 — Obra dashboard (8a: 8 first-level kinds; 8b: peripherals still null)
  obra_ambiente: { resource: 'OBRA_AMBIENTE', edit: 'EDIT', delete: 'DELETE' },
  obra_andamento: { resource: 'OBRA_ANDAMENTO', edit: 'EDIT', delete: 'DELETE' },
  obra_area: { resource: 'OBRA_AREA', edit: 'EDIT', delete: 'DELETE' },
  obra_envolvido: { resource: 'OBRA_ENVOLVIDO', edit: 'EDIT', delete: 'DELETE' },
  obra_ponto: { resource: 'OBRA_PONTO', edit: 'EDIT', delete: 'DELETE' },
  obra_produto: { resource: 'OBRA_PRODUTO', edit: 'EDIT', delete: 'DELETE' },
  obra_servico: { resource: 'OBRA_SERVICO', edit: 'EDIT', delete: 'DELETE' },
  // Vistoria has no dedicated `recurso` row in the DB — historically gated by
  // OBRA_SERVICO (matches the local pass-through in ObraDashboard.tsx and the
  // os_vistoria → OS pattern from Phase 4).
  obra_vistoria: { resource: 'OBRA_SERVICO', edit: 'EDIT', delete: 'DELETE' },
  obra_tubo: { resource: 'OBRA_TUBO', edit: 'EDIT', delete: 'DELETE' },
  obra_cabo: { resource: 'OBRA_CABO', edit: 'EDIT', delete: 'DELETE' },
  obra_etapa: { resource: 'OBRA_ETAPA', edit: 'EDIT', delete: 'DELETE' },
  // Arquivo has no dedicated `recurso` row in the DB — historically gated by
  // OBRA (the parent entity that owns the file). Same pattern as
  // os_vistoria → OS (Phase 4) and obra_vistoria → OBRA_SERVICO (Phase 8a).
  obra_arquivo: { resource: 'OBRA', edit: 'EDIT', delete: 'DELETE' },
  obra_produto_status: { resource: 'OBRA_PRODUTO_STATUS', edit: 'EDIT', delete: 'DELETE' },

  // Phase 9a — Cadastros leaves (8 kinds, no lookup deps)
  departamento: { resource: 'DEPARTAMENTO', edit: 'EDIT', delete: 'DELETE' },
  estado: { resource: 'ESTADO', edit: 'EDIT', delete: 'DELETE' },
  ambiente_tipo: { resource: 'AMBIENTE_TIPO', edit: 'EDIT', delete: 'DELETE' },
  ponto_tipo_grupo: { resource: 'PONTO_TIPO_GRUPO', edit: 'EDIT', delete: 'DELETE' },
  // VistoriaTipo has no dedicated `recurso` row in the DB — historically gated
  // by SERVICO_TIPO (matches `recurso="SERVICO_TIPO"` in VistoriaTipo.tsx and
  // mirrors the obra_vistoria → OBRA_SERVICO pattern from Phase 8a).
  vistoria_tipo: { resource: 'SERVICO_TIPO', edit: 'EDIT', delete: 'DELETE' },
  atraso_motivo: { resource: 'ATRASO_MOTIVO', edit: 'EDIT', delete: 'DELETE' },
  // FileCategoria has no dedicated `recurso` row in the DB and the parent list
  // (FileCategoria.tsx) intentionally does NOT pass `recurso=` — gating is
  // tela-level via ProtectedView with ADMIN_TOOLS. Keeping `null` preserves
  // today's behavior (renderer falls back to `{ canEdit: true, canDelete: true }`).
  file_categoria: null,
  modulo: { resource: 'MODULO', edit: 'EDIT', delete: 'DELETE' },

  // Phase 9b — Cadastros with lookup deps
  cidade: { resource: 'CIDADE', edit: 'EDIT', delete: 'DELETE' },
  fornecedor: { resource: 'FORNECEDOR', edit: 'EDIT', delete: 'DELETE' },
  ponto_tipo: { resource: 'PONTO_TIPO', edit: 'EDIT', delete: 'DELETE' },
  servico_tipo: { resource: 'SERVICO_TIPO', edit: 'EDIT', delete: 'DELETE' },
  profissional_tipo: { resource: 'PROFISSIONAL_TIPO', edit: 'EDIT', delete: 'DELETE' },
  // TiposCabo's parent list passes `recurso="TELA_TIPOS_CABO"` which doesn't
  // exist in the DB (`CABO_TIPO` is the closest real row but mapping there
  // would tighten the gate vs today). Keeping `null` preserves today's
  // tela-level access pattern.
  tipos_cabo: null,
  produto: { resource: 'PRODUTO', edit: 'EDIT', delete: 'DELETE' },

  // Phase 10 — Financeiro
  lancamento: { resource: 'FINANCEIRO_LANCAMENTO', edit: 'EDIT', delete: 'DELETE' },
  // Movimento has no dedicated `recurso` row in the DB — historically gated by
  // FINANCEIRO_LANCAMENTO (matches `recurso="FINANCEIRO_LANCAMENTO"` in
  // Movimento.tsx and the cumulative sub-table-inherits-parent precedent:
  // obra_arquivo → OBRA, obra_vistoria → OBRA_SERVICO, comissao_detalhe → COMISSAO).
  movimento: { resource: 'FINANCEIRO_LANCAMENTO', edit: 'EDIT', delete: 'DELETE' },
  forma_pagamento: { resource: 'FORMA_PAGAMENTO', edit: 'EDIT', delete: 'DELETE' },

  // Phase 11a — Admin/Misc simple flips + session drawer
  perfil: { resource: 'PERFIL', edit: 'EDIT', delete: 'DELETE' },
  usuario: { resource: 'USUARIO', edit: 'EDIT', delete: 'DELETE' },
  tarefa: { resource: 'TAREFA', edit: 'EDIT', delete: 'DELETE' },
  // MeuPerfil is a session drawer: any authenticated user → own profile.
  // No `recurso` row exists for it (and shouldn't — gating self-edit by a
  // resource permission would be odd). Drawer enforces "can only edit own
  // record" via useAuth() internally; null preserves that today's behavior.
  meu_perfil: null,
  // Phase 11b — LogPermissao has no dedicated `recurso` row; the parent list
  // (LogPermissao.tsx) passes `recurso="AUDITORIA"` and the drawer is read-only.
  // Cumulative sub-table-inherits-parent precedent (obra_arquivo→OBRA,
  // comissao_detalhe→COMISSAO, movimento→FINANCEIRO_LANCAMENTO).
  log_permissao: { resource: 'AUDITORIA', edit: 'EDIT', delete: 'DELETE' },
  // Phase 11c — TransitionRule has no `recurso` row in DB; the parent custom
  // view (TransitionRulesManager) is gated tela-level by ADMIN_TOOLS. null
  // preserves today's behavior (drawer-internal CRUD with no resource gate
  // beyond the tela). Precedent: file_categoria 9a, tipos_cabo 9b.
  transition_rule: null,
};

export type HasPermissionFn = (resource: string, action?: string) => boolean;

/**
 * Resolve `{ canEdit, canDelete }` for a drawer kind using the auth helper.
 *
 * Kinds with `null` in `DRAWER_PERMISSIONS` are treated as ungated — both
 * flags return `true` so the drawer's internal logic remains in charge.
 * Callers pass the resulting object directly to drawer `permissions` props.
 */
export function resolveDrawerPermissions(
  kind: OverlayDrawerKind,
  hasPermission: HasPermissionFn,
): { canEdit: boolean; canDelete: boolean } {
  const entry = DRAWER_PERMISSIONS[kind];
  if (!entry) return { canEdit: true, canDelete: true };
  return {
    canEdit: hasPermission(entry.resource, entry.edit),
    canDelete: hasPermission(entry.resource, entry.delete),
  };
}

/**
 * Drawer kinds whose channel gate (JOSH-91) uses a different recurso than
 * their edit/delete contract. Kept apart from DRAWER_PERMISSIONS on purpose:
 * mapping `os_report_review` to OS there would gate its internal writes by
 * OS edit. Every other kind falls back to its DRAWER_PERMISSIONS resource, so
 * new kinds stay covered without a second exhaustive map.
 */
const CANAL_RECURSO_OVERRIDES: Partial<Record<OverlayDrawerKind, string>> = {
  os_report_review: 'OS',
};

/**
 * Recurso whose view channel decides whether this drawer kind shows the
 * "available in the Joshua app" notice; null for kinds without app-level
 * gating (the drawer enforces access itself).
 */
export function resolveCanalRecurso(kind: OverlayDrawerKind): string | null {
  return CANAL_RECURSO_OVERRIDES[kind] ?? DRAWER_PERMISSIONS[kind]?.resource ?? null;
}
